import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_FEATURED_POSTS, GET_LATEST_POSTS } from '../../graphql/queries';

const Banner = () => {
    const publicUrl = process.env.PUBLIC_URL + '/';

    // Fetch featured/sticky posts for main banner
    const { data: featuredData } = useQuery(GET_FEATURED_POSTS, {
        variables: { first: 1 }
    });

    // Fetch latest posts for the grid
    const { data: latestData, loading, error } = useQuery(GET_LATEST_POSTS, {
        variables: { first: 5 }
    });

    // Get featured post (first one) or fallback to latest
    const mainPost = featuredData?.posts?.nodes?.[0] || latestData?.posts?.nodes?.[0];

    // Get grid posts (skip the first one if we're using latest posts)
    const gridPosts = latestData?.posts?.nodes?.slice(
        featuredData?.posts?.nodes?.length > 0 ? 0 : 1,
        5
    ) || [];

    // Helper function to format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    };

    // Helper function to get excerpt
    const getExcerpt = (excerpt, maxLength = 150) => {
        if (!excerpt) return '';
        const text = excerpt.replace(/<[^>]*>/g, '');
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    // Helper function to get featured image URL
    const getFeaturedImage = (post, fallbackImage = 'assets/img/banner/1.png') => {
        return post?.featuredImage?.node?.sourceUrl || publicUrl + fallbackImage;
    };

    // Helper function to get category
    const getFirstCategory = (post) => {
        return post?.categories?.nodes?.[0] || { name: 'Uncategorized', slug: 'uncategorized' };
    };

    if (loading) {
        return (
            <div className="banner-area banner-inner-1 bg-black">
                <div className="banner-inner pt-5">
                    <div className="container">
                        <div className="row">
                            <div className="col-12 text-center text-white">
                                <p>Loading...</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        console.error('Banner Error:', error);
        return (
            <div className="banner-area banner-inner-1 bg-black">
                <div className="banner-inner pt-5">
                    <div className="container">
                        <div className="row">
                            <div className="col-12 text-center text-white">
                                <p>Unable to load posts. Please try again later.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="banner-area banner-inner-1 bg-black">
            {/* banner area start */}
            <div className="banner-inner pt-5 go-top">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-6">
                            <div className="thumb after-left-top">
                                <img
                                    src={getFeaturedImage(mainPost)}
                                    alt={mainPost?.featuredImage?.node?.altText || mainPost?.title || 'Featured post'}
                                />
                            </div>
                        </div>
                        <div className="col-lg-6 align-self-center">
                            <div className="banner-details mt-4 mt-lg-0">
                                <div className="post-meta-single mb-2">
                                    <ul>
                                        <li>
                                            <Link className="tag-base tag-blue" to={`/category/${getFirstCategory(mainPost).slug}`} style={{borderRadius: '20px',  fontSize: '16px'}}>
                                                {getFirstCategory(mainPost).name}
                                            </Link>
                                        </li>
                                        <li>
                                            <i className="fa fa-clock-o" />
                                            {mainPost?.date ? formatDate(mainPost.date) : 'N/A'}
                                        </li>
                                    </ul>
                                </div>
                                <h2 dangerouslySetInnerHTML={{ __html: mainPost?.title || 'Loading...' }} />
                                <p>{getExcerpt(mainPost?.excerpt)}</p>
                                <Link className="btn btn-blue" to={`/blog/${mainPost?.slug || ''}`}>Read More</Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* banner area end */}

            <div className="container go-top">
                <div className="row">
                    {gridPosts.map((post, index) => {
                        const category = getFirstCategory(post);
                        const tagClass = index % 2 === 0 ? 'tag-blue' : 'tag-orange';

                        return (
                            <div className="col-lg-3 col-sm-6" key={post.id}>
                                <div className="single-post-wrap style-white">
                                    <div className="thumb">
                                        <img
                                            src={getFeaturedImage(post, `assets/img/post/${index + 1}.png`)}
                                            alt={post?.featuredImage?.node?.altText || post.title}
                                        />
                                    </div>
                                    <div className="details">
                                        <div className="post-meta-single mb-2">
                                            <ul>
                                                <li>
                                                    <Link className={`tag-base ${tagClass}`} to={`/category/${category.slug}`} style={{borderRadius: '20px',  fontSize: '12px'}}>
                                                        {category.name}
                                                    </Link>
                                                </li>
                                                <li>
                                                    <i className="fa fa-clock-o" />
                                                    {formatDate(post.date)}
                                                </li>
                                            </ul>
                                        </div>
                                        <h6 className="title">
                                            <Link to={`/blog/${post.slug}`} dangerouslySetInnerHTML={{ __html: post.title }} />
                                        </h6>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default Banner;
